import { WebSocketController } from '@services/web-socket.controller';
import { NotificationsChangedEvent, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { UnreadCountersService } from '@app/services/unread-counters.service';
import { DestroyRef, EventEmitter, inject, Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { getToken, isSupported, Messaging, MessagePayload, onMessage } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import {
  AvatarOrderDataResponse,
  Client,
  NotificationResponse,
} from '@api/client';
import { PlatformService } from './platform.service';
import CallKit, { Order } from '@plugins/android_callkit/callkit.plugin';
import { PushRegistrationService } from '@app/services/push-registration.service';
import { BehaviorSubject, filter, first, firstValueFrom, Observable, Subject, map } from 'rxjs';
import {
  CONTENT_MODE,
  CONTENT_TYPE,
  NOTIFICATION_TYPE,
} from '@constants/app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, ParsedToken } from './auth.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { VoipNotificationsService } from './voip.notifications.service';
import { PermissionStatus as PushPermissionStatus } from '@capacitor/push-notifications/dist/esm/definitions';
import { PermissionStatus as LocalPermissionStatus } from '@capacitor/local-notifications/dist/esm/definitions';
import { PermissionState } from '@capacitor/core/types/definitions';
import { AlertController, AlertOptions } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { AppStateChangeService } from './app-state-change.service';
import { ChatsService } from './chats.service';
import { ToastService } from './toast.service';
import { environment } from '@environments/environment';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { NavigationStore } from '@services/navigation.store';
import { buildProfilePath } from '@utils/profile-path';
import { AvatarOrderService } from '@services/avatar-order.service';
import { CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { ProfileMediaOpenResult, ProfileMediaNavigationService } from '@services/profile-media-navigation.service';
import {
  PushCallPayload,
  PushCallCancelPayload,
  PushAvatarOrderPayload,
  PushChatPayload,
  PushCommentPayload,
  PushMessagePayload,
  PushNewSubscriberPayload,
  PushOfferCreateTaskPayload,
  PushPayloadInterfaces,
  PushPaymentPayload,
  PushReplyPayload,
  PushSubscriptionPayload,
  PushTaskPayload,
  PushUniversalPayload,
  PushUserActionPayload,
} from './push-payload.interfaces';
import { AppNavigationService } from '@state-routing';
import { WalletEventDeduplicationService } from '@app/services/wallet-event-deduplication.service';
import { DisputeAssignmentService } from '@app/services/dispute-assignment.service';
import { getBrowserPushTokenWithRetry, waitForBrowserPushWorker } from '@app/services/browser-push-registration';

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly unreadCounters = inject(UnreadCountersService);
  private readonly socket = inject(WebSocketController);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly injector: Injector = inject(Injector);
  private readonly router: Router = inject(Router);
  private readonly client: Client = inject(Client);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly pushRegistration = inject(PushRegistrationService);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly authService: AuthService = inject(AuthService);
  private readonly voipNotificationsService: VoipNotificationsService = inject(VoipNotificationsService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly appStateChangeService: AppStateChangeService = inject(AppStateChangeService);
  private readonly alertController: AlertController = inject(AlertController);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly chatsService: ChatsService = inject(ChatsService);
  private readonly diagnostic: Diagnostic = inject(Diagnostic);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly avatarOrderService: AvatarOrderService = inject(AvatarOrderService);
  private readonly profileMediaNavigationService = inject(ProfileMediaNavigationService);
  private readonly appNavigation: AppNavigationService = inject(AppNavigationService);
  private readonly walletEventDeduplicationService: WalletEventDeduplicationService = inject(WalletEventDeduplicationService);

  public readonly countNotifications = this.unreadCounters.notificationCount;
  public readonly listRefreshRequested$ = this.unreadCounters.refreshRequested$;
  public readonly listRequestsCancelled$ = this.unreadCounters.requestsCancelled$;
  private readonly listChanges = new Subject<NotificationsChangedEvent>();
  public readonly listChanges$ = this.listChanges.asObservable();

  public closeAlertNotificationPermissionsEvent: EventEmitter<void> = new EventEmitter<void>();
  private readonly isPushNotificationsAvailable: boolean = false;
  private _userDeviceId: number | null = null;
  private _pushToken: string | null = null;
  private pushTokenRegistration: object | null = null;
  private alertNotificationPermissions: HTMLIonAlertElement;
  #notificationsSubject: BehaviorSubject<NotificationResponse[]> = new BehaviorSubject<NotificationResponse[]>([]);
  public notifications$: Observable<NotificationResponse[]> = this.#notificationsSubject.asObservable();
  #pushPermissionGrantedSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  public pushPermissionGranted$: Observable<boolean | null> = this.#pushPermissionGrantedSubject.asObservable();
  private browserPermissionStatus: PermissionStatus | null = null;
  private readonly chatNotificationTypes: ReadonlySet<number> = new Set<number>([
    NOTIFICATION_TYPE.OFFER,
    NOTIFICATION_TYPE.EXTRA_WORK,
    NOTIFICATION_TYPE.CHAT,
    NOTIFICATION_TYPE.EXTRA_WORK_REJECTED,
    NOTIFICATION_TYPE.EXTRA_WORK_OFFER_EXTRA_PAY,
    NOTIFICATION_TYPE.EXTRA_WORK_OWNER_APPROVE,
    NOTIFICATION_TYPE.EXTRA_WORK_PAID,
    NOTIFICATION_TYPE.EXTRA_WORK_WORKER_APPROVE,
    NOTIFICATION_TYPE.EXTRA_WORK_PENDING,
    NOTIFICATION_TYPE.EXTRA_WORK_WORKER_ADDED_RESULTS,
    NOTIFICATION_TYPE.EXTRA_WORK_WAIT_PAY,
    NOTIFICATION_TYPE.DISPUTE_CANCELED,
    NOTIFICATION_TYPE.EXTRA_WORK_DELETED,
    NOTIFICATION_TYPE.EXTRA_WORK_OWNER_APPROVE_RESULTS,
    NOTIFICATION_TYPE.PAYMENT_CANCELED,
    NOTIFICATION_TYPE.PAYMENT_AFTER_5_DAYS,
  ]);
  private initialNativeNotificationActionPhase: boolean = true;
  private pushListenersReady: boolean = false;
  private messaging: Messaging | null = null;
  private firebaseMessageUnsubscribe: (() => void) | null = null;
  private browserPushAbort: AbortController | null = null;
  private browserPushInitialization: Promise<void> | null = null;

  private readonly permissionsChangeHandler = (): void => {
    void this.syncPushPermissionState();
  };

  public get isListForeground(): boolean {
    return this.unreadCounters.isForeground;
  }

  public get notifications(): NotificationResponse[] {
    return this.#notificationsSubject.value;
  }

  public set notifications(value: NotificationResponse[]) {
    this.#notificationsSubject.next(value);
  }

  constructor() {
    this.isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications');

    if ((typeof navigator !== 'undefined') && !this.isPushNotificationsAvailable && 'serviceWorker' in navigator) {
      this.addServiceWorkerListeners();
    }

    if (this.isPushNotificationsAvailable) {
      void this.addNotificationsListeners();
    }

    if (this.platformService.isBrowser) {
      void this.initBrowserNotificationsPermissionWatcher();
    }

    this.authService.parsedToken$.pipe(
      distinctUntilChanged((prev: ParsedToken, curr: ParsedToken): boolean => prev?.userId === curr?.userId),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (parsedToken: ParsedToken | null): Promise<void> => {
      const sessionVersion = this.authService.sessionVersion;
      this._userDeviceId = null;
      this.cancelBrowserPushRegistration();
      if (!parsedToken) return;
      await this.initializePushNotifications();
      if (sessionVersion !== this.authService.sessionVersion) return;

      if (this.platformService.isDevice && this.platformService.isIos) {
        await this.voipNotificationsService.initializeVoipPushNotifications();
      }
    });

    this.socket.notificationEvent.pipe(
      filter(event => event.type === SOCKET_EVENT_NAMES.NOTIFICATIONS_CHANGED),
      map((event): NotificationsChangedEvent => event.data),
      filter(event => event?.user_id === this.authService.parsedToken?.userId),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => this.applyListChanges(event));

    this.authService.parsedToken$.pipe(
      distinctUntilChanged((previous, current): boolean => previous?.userId === current?.userId),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((): void => {
      this.notifications = [];
    });

    this.closeAlertNotificationPermissionsEvent.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (): Promise<void> => {
      await this.alertNotificationPermissions?.dismiss();
    });

    void this.syncPushPermissionState();
  }

  public applyListChanges(event: NotificationsChangedEvent): void {
    if (event.user_id !== this.authService.parsedToken?.userId) return;
    const changes = new Map(event.changes.map(change => [change.id, change.is_read]));
    this.notifications = this.notifications
      .filter(item => changes.get(item.id) !== null)
      .map(item => changes.has(item.id)
        ? new NotificationResponse({...item, is_read: changes.get(item.id)})
        : item);
    this.listChanges.next(event);
  }

  private async initBrowserNotificationsPermissionWatcher(): Promise<void> {
    if ((typeof navigator === 'undefined') || !('permissions' in navigator) || !navigator.permissions.query) return;

    try {
      const permissionStatus: PermissionStatus = await navigator.permissions.query({name: 'notifications' as PermissionName});
      this.browserPermissionStatus = permissionStatus;

      if (typeof permissionStatus.addEventListener === 'function') {
        permissionStatus.addEventListener('change', this.permissionsChangeHandler);
      } else {
        permissionStatus.onchange = this.permissionsChangeHandler;
      }
    } catch (error) {
      console.error('Error initializing browser notifications permission watcher', error);
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.cancelBrowserPushRegistration();
    if (this.browserPermissionStatus) {
      if (typeof this.browserPermissionStatus.removeEventListener === 'function') {
        this.browserPermissionStatus.removeEventListener('change', this.permissionsChangeHandler);
      } else {
        this.browserPermissionStatus.onchange = null;
      }
      this.browserPermissionStatus = null;
    }
    if (this.isPushNotificationsAvailable) {
      await PushNotifications.removeAllListeners();
    }
    this.firebaseMessageUnsubscribe?.();
    this.firebaseMessageUnsubscribe = null;
    await LocalNotifications.removeAllListeners();
  }

  public async checkPushPermissionsWithDevice(callbackCancel: Function = null, callbackConfirm: Function = null): Promise<void> {
    const {receive} = await this.getPushPermissions();

    if (receive === 'denied') {
      console.warn('🟠 Push notifications permission is denied');
      await this.showAlertToEnableNotificationPermissions(callbackCancel, callbackConfirm);
      return;
    } else {
      if (typeof callbackConfirm === 'function') await callbackConfirm();
    }
  }

  public async checkNotificationPermissionStatusWithBrowser(callbackCancel: Function = null, callbackConfirm: Function = null): Promise<void> {
    const isGranted: boolean = await this.getNotificationPermissionStatusWithBrowser();

    if (!isGranted) {
      if (typeof callbackCancel === 'function') await callbackCancel();
    } else {
      if (typeof callbackConfirm === 'function') await callbackConfirm();
    }
  }

  public async initializePushNotifications(): Promise<void> {
    try {
      if (this.isPushNotificationsAvailable) {
        await this.initPushNotifications();
      } else {
        await this.initFirebaseMessaging();
      }
    } finally {
      await this.syncPushPermissionState();
    }
  }

  public async syncPushPermissionState(): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      if (this.platformService.isDevice && this.isPushNotificationsAvailable) {
        const permissions: PushPermissionStatus = await PushNotifications.checkPermissions();
        this.#pushPermissionGrantedSubject.next(permissions.receive === 'granted');
        return;
      }

      if (this.platformService.isBrowser && (typeof Notification !== 'undefined')) {
        this.#pushPermissionGrantedSubject.next(Notification.permission === 'granted');
        return;
      }

      this.#pushPermissionGrantedSubject.next(null);
    } catch (error) {
      console.error('Error checking push notification permissions', error);
      this.#pushPermissionGrantedSubject.next(false);
    }
  }

  public async requestPushPermissions(): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      if (this.platformService.isDevice && this.isPushNotificationsAvailable) {
        const result: PushPermissionStatus = await PushNotifications.requestPermissions();

        if (result.receive === 'granted') {
          await this.registerPushIfNeedAndSendPushToken();
          this.#pushPermissionGrantedSubject.next(true);
        } else {
          this.#pushPermissionGrantedSubject.next(false);

          if (result.receive === 'denied') {
            await this.openNotificationSettings();
          }
        }
      } else if (this.platformService.isBrowser && (typeof Notification !== 'undefined')) {
        const permission: NotificationPermission = await Notification.requestPermission();

        if (permission === 'granted') {
          await this.initFirebaseMessagingIfNeedAndSendToken();
          this.#pushPermissionGrantedSubject.next(true);
        } else {
          this.#pushPermissionGrantedSubject.next(false);

          if (permission === 'denied') {
            await this.toastService.warning(this.translate.instant('notificationService.toasts.allowBrowserNotifications'));
          }
        }
      }
    } catch (error) {
      console.error('Error requesting push notification permissions', error);
      await this.toastService.warning(this.translate.instant('notificationService.toasts.errorRegisterPushNotifications'));
    } finally {
      await this.syncPushPermissionState();
    }
  }

  public getUserDeviceId(): number | null {
    return this._userDeviceId;
  }

  public async deleteUserDevice(): Promise<void> {
    if (!this.userDeviceId) return console.error('User device ID is not defined!');

    try {
      await firstValueFrom(this.client.userDeviceDelete(this.userDeviceId));
      this.userDeviceId = null;
    } catch (error) {
      console.error('Error on delete user device', error);
    }
  }

  private async getPushPermissions(): Promise<{ receive: PermissionState; display: PermissionState }> {
    const pushNotificationsPermissions: PushPermissionStatus = await PushNotifications.checkPermissions();
    const localNotificationsPermissions: LocalPermissionStatus = await LocalNotifications.checkPermissions();
    return {receive: pushNotificationsPermissions.receive, display: localNotificationsPermissions.display};
  }

  public async showAlertToEnableNotificationPermissions(callbackCancel: Function = null, callbackConfirm: Function = null): Promise<void> {
    if (this.alertNotificationPermissions || typeof window === 'undefined') return;

    const opts: AlertOptions = {
      header: this.translate.instant('notificationService.alerts.permissions.header'),
      buttons: [
        {
          text: this.translate.instant('notificationService.alerts.permissions.buttons.cancel'),
          role: 'cancel',
          handler: async () => {
            if (typeof callbackCancel === 'function') await callbackCancel();
          }
        },
        {
          text: this.translate.instant('notificationService.alerts.permissions.buttons.settings'),
          role: 'confirm',
          handler: async () => {
            await this.openNotificationSettings(callbackCancel, callbackConfirm);
          },
        }
      ],
      backdropDismiss: false,
    };

    this.alertNotificationPermissions = await this.alertController.create(opts);
    await this.alertNotificationPermissions?.present();
    await this.alertNotificationPermissions?.onDidDismiss();
    this.alertNotificationPermissions = null;
  }

  private async openNotificationSettings(callbackCancel: Function = null, callbackConfirm: Function = null): Promise<void> {
    await this.diagnostic.switchToSettings();

    this.appStateChangeService.appResume$.pipe(
      first((isActive: boolean): boolean => isActive),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (): Promise<void> => {
      try {
        const {receive, display} = await this.getPushPermissions();
        const isGranted: boolean = (receive === 'granted');
        this.#pushPermissionGrantedSubject.next(isGranted);

        if (isGranted) {
          console.log('Push notification permission:', {receive, display});
          await this.registerPush();
          /* sendPushToken in listener event 'registration' */

          if (typeof callbackConfirm === 'function') await callbackConfirm();
        } else {
          console.warn('Push notification permission:', {receive, display});
          await this.toastService.warning(this.translate.instant('notificationService.toasts.permissionNotificationsNotGranted'));

          if (typeof callbackCancel === 'function') await callbackCancel();
        }
        await this.syncPushPermissionState();
      } catch (error) {
        console.error('Error on register Push notification', error);
        await this.toastService.warning(this.translate.instant('notificationService.toasts.errorRegisterPushNotifications'));
        this.#pushPermissionGrantedSubject.next(false);
        await this.syncPushPermissionState();

        if (typeof callbackCancel === 'function') await callbackCancel();
      }
    });
  }

  private set userDeviceId(userDeviceId: number) {
    this._userDeviceId = userDeviceId;
  }

  private get userDeviceId(): number | null {
    return this._userDeviceId;
  }

  private set pushToken(pushToken: string) {
    this._pushToken = pushToken;
  }

  private get pushToken(): string | null {
    return this._pushToken;
  }

  private addServiceWorkerListeners(): void {
    if (this.isPushNotificationsAvailable || (typeof navigator === 'undefined') || !('serviceWorker' in navigator)) return;

    // Listen to service worker messages sent via postMessage()
    navigator.serviceWorker.addEventListener('message', async (event): Promise<void> => {
      if (!event.data) return console.error('[ServiceWorker] message data is not defined!');

      console.log('\x1b[35m' + `[ServiceWorker] message:` + '\x1b[0m', event.data.data);

      await this.navigate(event.data);
    });
  }

  private async initPushNotifications(): Promise<void> {
    if (this.platformService.isDevice && this.platformService.isAndroid) {
      await this.createNotificationChannels();
    }
    await this.addNotificationsListeners();
    await this.requestsNotificationsPermissions();
  }

  public async initFirebaseMessagingIfNeedAndSendToken(): Promise<void> {
    try {
      await this.initFirebaseMessaging();
    } catch (error) {
      console.error('Error on register push if need and send push token', error);
      throw error;
    }
  }

  private async createNotificationChannels(): Promise<void> {
    if (!this.platformService.isDevice || !this.platformService.isAndroid) return;

    try {
      await PushNotifications.createChannel({
        id: 'sound-vibration-channel',
        name: 'Sound and vibration',
        description: 'Channel for notifications with sound and vibration',
        sound: 'default',
        importance: 5,
        vibration: true,
        visibility: 1,
        lights: true,
      });

      await PushNotifications.createChannel({
        id: 'call-sound-vibration-channel',
        name: 'Call sound and vibration',
        description: 'Channel for call notifications with sound and vibration',
        // sound: 'avatar2', // TODO сделать для звонка обычный звук, а для заказа аватара этот звук
        sound: 'default',
        importance: 5,
        vibration: true,
        visibility: 1,
        lights: true,
      });
    } catch (error) {
      console.error('Error on create notification channels', error);
    }
  }

  private async addNotificationsListeners(): Promise<void> {
    if (this.pushListenersReady) {
      return;
    }
    const initialActionPhase: boolean = this.initialNativeNotificationActionPhase;
    this.pushListenersReady = true;
    try {
      await PushNotifications.removeAllListeners();
      await LocalNotifications.removeAllListeners();

      // On success, we should be able to receive notifications
      await PushNotifications.addListener('registration', async (token: Token): Promise<void> => {
        // alert(`Push registration, token: ${JSON.stringify(token.value)}`);
        // console.log(`Push registration, token: ${token.value}`);

        this.pushToken = token.value;
        await this.sendPushToken(this.pushToken);
      });

      // Some issue with our setup and push will not work
      await PushNotifications.addListener('registrationError', (error: any): void => {
        // alert(`Error push registration: ${JSON.stringify(error)}`);
        console.error(`Error push registration, error: ${error}`);
      });

      // Show us the notification payload if the app is open on our device
      await PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema): Promise<void> => {
        // alert(`Push notification received: ${JSON.stringify(notification)}`);
        console.log('Push notification received, notification:', this.toLogString(notification));
        console.log('Push notification received, data:', this.toLogString(notification?.data));

        await this.scheduleNotification(notification);
      });

      // Method called when tapping on a notification
      await PushNotifications.addListener('pushNotificationActionPerformed', async (notification: ActionPerformed): Promise<void> => {
        // alert(`Push action performed: ${JSON.stringify(notification)}`);
        console.log('Push action performed, notification:', this.toLogString(notification));
        console.log('Push action performed, data:', this.toLogString(notification?.notification?.data));

        await this.navigate(notification.notification.data, this.initialNativeNotificationActionPhase);
      });

      // Show us the local notification payload if the app is open on our device
      await LocalNotifications.addListener('localNotificationReceived', (notification): void => {
        // alert(`Local notification received: ${JSON.stringify(notification)}`);
        console.log(`Local notification received: ${notification}`);
      });

      // Method called when tapping on a local notification
      await LocalNotifications.addListener('localNotificationActionPerformed', async (notification): Promise<void> => {
        // alert(`Local notification received: ${JSON.stringify(notification)}`);
        console.log(`Local notification received: ${notification}`);

        await this.navigate(notification.notification.extra, this.initialNativeNotificationActionPhase);
      });
      if (initialActionPhase) {
        this.initialNativeNotificationActionPhase = false;
      }

      await CallKit.addListener('answer', async (order: Order): Promise<void> => {
        if (!order) return console.error('CallKit answer. No order!');

        // alert(`CallKit answer, order ID: ${JSON.stringify(order)}`);
        console.log(`CallKit answer, order ID: ${order}`);

        const nativeType: number | null = this.normalizePayloadNumber(order.type ?? null);
        const orderType: number | null = this.normalizeIncomingOrderType(nativeType);
        await this.goToCall(order.id, orderType);
      });
    } catch (error) {
      this.pushListenersReady = false;
      console.error('Error on add notifications listeners', error);
    } finally {
      this.initialNativeNotificationActionPhase = false;
    }
  }

  private async scheduleNotification(notification: PushNotificationSchema): Promise<void> {
    const walletEventId: string | null = this.getWalletEventId(notification?.data);
    try {
      if (this.shouldSuppressChatNotification(notification)) return;
      if (this.isCallCancelNotification(notification)) return;
      if (!this.walletEventDeduplicationService.claim('push', walletEventId)) return;

      // Create a local notification
      await LocalNotifications.schedule({
        notifications: [{
          title: notification.title,
          body: notification.body,
          extra: notification.data,
          id: this.getLocalNotificationId(walletEventId),
          smallIcon: 'ic_stat_onesignal_default',
          iconColor: this.isDarkTheme() ? '#ffffff' : '#09203e',
          // Add any other desired options for the notification
        }]
      });
    } catch (error) {
      this.walletEventDeduplicationService.release('push', walletEventId);
      console.error('Error on schedule notification', error);
    }
  }

  private getWalletEventId(data: unknown): string | null {
    if (!this.isRecord(data)) return null;
    return this.getStringField(data, 'walletEventId');
  }

  private getLocalNotificationId(walletEventId: string | null): number {
    if (walletEventId === null) return Math.round(Math.random() * 10000);

    let hash: number = 0;
    for (let index: number = 0; index < walletEventId.length; index++) {
      hash = (Math.imul(hash, 31) + walletEventId.charCodeAt(index)) & 0x7fffffff;
    }
    return hash || 1;
  }

  private isCallCancelNotification(notification: PushNotificationSchema): boolean {
    return this.isCallCancelPayload(notification?.data);
  }

  private isCallCancelPayload(data: unknown): data is PushCallCancelPayload {
    if (!this.isRecord(data)) return false;
    const callAction = this.getStringField(data, 'call_action');
    const callReason = this.getStringField(data, 'call_reason');
    if (callAction === 'cancel') return true;
    if (callReason === 'answered_elsewhere' || callReason === 'rejected' || callReason === 'canceled') return true;

    const typeValue = this.getNumberField(data, 'type');
    if (typeValue === NOTIFICATION_TYPE.CALL_CANCELLED) {
      const isMissedCallRaw = data['isMissedCall'];
      const isMissedCall = (isMissedCallRaw === true) || (this.normalizePayloadNumber(isMissedCallRaw ?? null) === 1);
      if (!isMissedCall) return true;
    }
    return false;
  }

  private getStringField(data: Record<string, unknown>, key: string): string | null {
    const value = data[key];
    return typeof value === 'string' ? value : null;
  }

  private getNumberField(data: Record<string, unknown>, key: string): number | null {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  private normalizePayloadNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  private normalizePayloadString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized: string = value.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
  }

  private shouldSuppressChatNotification(notification: PushNotificationSchema): boolean {
    if (!this.appStateChangeService.appIsActive) return false;

    const data = notification?.data;
    if (!this.isRecord(data)) return false;

    const notificationType = this.getNumberField(data, 'type');
    if (notificationType === null) return false;
    if (notificationType !== NOTIFICATION_TYPE.MESSAGE && !this.chatNotificationTypes.has(notificationType)) return false;

    const chatId = this.getNumberField(data, 'chatId');
    if (chatId === null) return false;

    const activeChatId = this.chatsService.activeChatId;
    if (activeChatId == null) return false;

    return chatId === activeChatId;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return (typeof value === 'object') && (value !== null);
  }

  private toLogString(value: unknown): string {
    const seen = new WeakSet<object>();
    try {
      return JSON.stringify(value, (_key: string, entry: unknown): unknown => {
        if (typeof entry === 'bigint') {
          return entry.toString();
        }
        if (typeof entry === 'object' && entry !== null) {
          if (seen.has(entry)) {
            return '[Circular]';
          }
          seen.add(entry);
        }
        return entry;
      });
    } catch {
      return String(value);
    }
  }

  private isDarkTheme(): boolean {
    return (typeof document !== 'undefined') ? document.body.classList.contains('dark') : true;
  }

  private async requestsNotificationsPermissions(): Promise<void> {
    // Request permission to use push notifications
    try {
      const result: PushPermissionStatus = await PushNotifications.requestPermissions();

      switch (result.receive) {
        case 'granted':
          console.info('\x1b[32m' + 'Access to push notification granted.' + '\x1b[0m');
          await this.alertNotificationPermissions?.dismiss();
          this.#pushPermissionGrantedSubject.next(true);
          await this.registerPush();
          break;
        case 'prompt':
        case 'prompt-with-rationale':
          console.info('\x1b[33m' + 'The permission request for push notification is in a prompt state.' + '\x1b[0m');
          this.#pushPermissionGrantedSubject.next(false);
          break;
        case 'denied':
          console.info('\x1b[31m' + 'Access to push notification denied.' + '\x1b[0m');
          this.#pushPermissionGrantedSubject.next(false);
          break;
      }
    } catch (error) {
      console.error('Error on request Push notification permissions', error);
      this.#pushPermissionGrantedSubject.next(false);
    }

    // Request permission to show local notifications
    try {
      const result: LocalPermissionStatus = await LocalNotifications.requestPermissions();

      switch (result.display) {
        case 'granted':
          console.info('\x1b[32m' + 'Access to local notification granted.' + '\x1b[0m');
          break;
        case 'prompt':
        case 'prompt-with-rationale':
          console.info('\x1b[33m' + 'The permission request for local notification is in a prompt state.' + '\x1b[0m');
          break;
        case 'denied':
          console.info('\x1b[31m' + 'Access to local notification denied.' + '\x1b[0m');
          break;
      }
    } catch (error) {
      console.error('Error on request Local notification permissions', error);
    }
  }

  private async getFirebaseMessagingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
      type: 'module',
    });
    await waitForBrowserPushWorker(registration);
    return registration;
  }

  private async getFirebaseMessaging(): Promise<Messaging | null> {
    if (this.messaging) {
      return this.messaging;
    }

    const isMessagingSupported: boolean = await isSupported();
    if (!isMessagingSupported) {
      return null;
    }

    this.messaging = this.injector.get(Messaging, null);
    return this.messaging;
  }

  private async initFirebaseMessaging(): Promise<void> {
    if ((typeof navigator === 'undefined') || !('serviceWorker' in navigator)) {
      return;
    }
    if (this.browserPushInitialization) return this.browserPushInitialization;
    const userId = this.authService.parsedToken?.userId;
    if (!userId) return;
    const controller = new AbortController();
    this.browserPushAbort = controller;
    const isActive = (): boolean => !controller.signal.aborted && this.authService.parsedToken?.userId === userId;
    const operation = this.registerBrowserPush(controller.signal, isActive).finally((): void => {
      if (this.browserPushAbort === controller) this.browserPushInitialization = null;
    });
    this.browserPushInitialization = operation;
    return operation;
  }

  private async registerBrowserPush(signal: AbortSignal, isActive: () => boolean): Promise<void> {
    try {
      const messaging: Messaging | null = await this.getFirebaseMessaging();
      if (!messaging || !isActive()) return;

      const serviceWorkerRegistration: ServiceWorkerRegistration = await this.getFirebaseMessagingServiceWorkerRegistration();
      if (!isActive()) return;
      const token = await getBrowserPushTokenWithRetry(() => getToken(messaging, {serviceWorkerRegistration}), signal);
      if (!isActive() || !token) return;
      this.pushToken = token;
      await this.registerUserDevice(token, isActive, serviceWorkerRegistration);
    } catch (error) {
      if (isActive()) console.warn('Browser push registration failed', {
        name: error instanceof Error ? error.name : 'UnknownError',
      });
    }
  }

  private cancelBrowserPushRegistration(): void {
    if (this.isPushNotificationsAvailable) return;
    this.browserPushAbort?.abort();
    this.browserPushAbort = null;
    this.browserPushInitialization = null;
    this._pushToken = null;
    this._userDeviceId = null;
    this.firebaseMessageUnsubscribe?.();
    this.firebaseMessageUnsubscribe = null;
  }

  private async registerUserDevice(
    pushToken: string,
    isActive: () => boolean,
    serviceWorkerRegistration: ServiceWorkerRegistration,
  ): Promise<void> {
    if (!pushToken) return console.error('Token is not defined!');

    // alert(`AFS, user device registration, token: ${JSON.stringify(token)}`);
    // console.log(`AFS, user device registration, token: ${token}`);

    await this.sendPushToken(pushToken, isActive);

    if (!this.messaging || !isActive()) return;

    try {
      this.firebaseMessageUnsubscribe?.();
      this.firebaseMessageUnsubscribe = onMessage(this.messaging, async (message: MessagePayload): Promise<void> => {
        if (!isActive()) return;
        const messageData: Record<string, unknown> = message?.data ?? {};
        const notificationType: number | null = this.getNumberField(messageData, 'type');
        if (notificationType === NOTIFICATION_TYPE.CALL
          || (notificationType === NOTIFICATION_TYPE.CALL_CANCELLED && this.isCallCancelPayload(messageData))) {
          // A single worker owns ringing notifications in both foreground and background.
          const worker: ServiceWorker | null = serviceWorkerRegistration.active;
          if (!worker) {
            console.warn('Browser call notification worker unavailable');
            return;
          }
          worker.postMessage({kind: 'pyjam.audio-call-notification', data: messageData});
          return;
        }
        if (this.isCallCancelPayload(messageData)) return;

        const rawTitleCandidate: unknown = (typeof messageData['title'] === 'string') ? messageData['title'] : message?.notification?.title;
        const rawBodyCandidate: unknown = (typeof messageData['body'] === 'string') ? messageData['body'] : message?.notification?.body;

        const notificationTitle: string | undefined = (typeof rawTitleCandidate === 'string') ? rawTitleCandidate : undefined;
        const notificationBody: string | undefined = (typeof rawBodyCandidate === 'string') ? rawBodyCandidate : undefined;

        const enrichedData: Record<string, unknown> = {
          ...messageData,
          title: notificationTitle ?? '',
          body: notificationBody
        };
        const notificationOptions: NotificationOptions = {
          body: notificationBody ?? '',
          icon: 'assets/icon/logo.png',
          data: enrichedData,
        };

        if ((typeof window !== 'undefined') && ('Notification' in window) && (typeof Notification === 'function')) {
          if (Notification.permission !== 'granted') {
            console.warn('Notifications permission is not granted');
            return;
          }

          try {
            const browserNotification: Notification = new Notification(notificationTitle, notificationOptions);

            browserNotification.addEventListener('click', async (): Promise<void> => {
              await this.navigate(browserNotification.data);
            });

            return;
          } catch (notificationError) {
            if (!((notificationError instanceof TypeError) || (typeof DOMException !== 'undefined' && notificationError instanceof DOMException))) {
              console.error('Error showing notification in window context', notificationError);
              return;
            }
          }
        }

        if ((typeof navigator !== 'undefined') && ('serviceWorker' in navigator)) {
          try {
            const registration: ServiceWorkerRegistration = await navigator.serviceWorker.ready;
            await registration.showNotification(notificationTitle, notificationOptions);
            return;
          } catch (serviceWorkerError) {
            console.error('Error showing notification with service worker', serviceWorkerError);
            return;
          }
        }

        console.warn('Notifications API is not supported in this environment.');
      });
    } catch (error) {
      console.error('Error on register user device', error);
    }
  }

  private async sendPushToken(pushToken: string, isActive: () => boolean = (): boolean => true): Promise<void> {
    if (!pushToken) return console.error('Token is not defined!');

    const sessionVersion = this.authService.sessionVersion;
    if (!isActive()) return;
    const registration = {};
    this.pushTokenRegistration = registration;
    const isCurrent = (): boolean => this.pushTokenRegistration === registration
      && isActive() && sessionVersion === this.authService.sessionVersion;
    try {
      const deviceId = await this.pushRegistration.register(pushToken, 1, isCurrent);
      if (deviceId !== null && isCurrent()) {
        this.userDeviceId = deviceId;
      }
    } catch {
      console.warn('FCM push registration failed');
    }
  }

  private async navigate(data: unknown, coldStart: boolean = false): Promise<void> {
    if (!this.isRecord(data)) {
      console.error('Data is not defined!');
      return;
    }
    const payload: PushPayloadInterfaces = data as PushPayloadInterfaces;

    await this.ngZone.run(async (): Promise<void> => {
      const isFirebaseMessaging: NOTIFICATION_TYPE = (payload as any).isFirebaseMessaging;
      const notificationType: NOTIFICATION_TYPE = Number(payload.type);

      if (isFirebaseMessaging) return;
      if (!notificationType) {
        console.error('Notification type is not defined!');
        return;
      }

      switch (notificationType) {
        case NOTIFICATION_TYPE.MESSAGE:
          if (!this.isPushNotificationsAvailable) return;
          const messagePayload = payload as PushMessagePayload;
          const messageChatId = messagePayload.chatId;
          if (messageChatId == null) return console.error('chatId is not defined!');
          try {
            const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
            deliveredNotifications.notifications = deliveredNotifications.notifications
              .map((deliveredNotification) => payload.title == deliveredNotification.title ? deliveredNotification : null)
              .filter(Boolean);

            await PushNotifications.removeDeliveredNotifications(deliveredNotifications);
          } catch (error) {
            console.error('Error on remove delivered notifications', error);
          } finally {
            await this.navigateToPushTarget(['/chats', messageChatId], coldStart);
          }
          break;
        case NOTIFICATION_TYPE.COMMENT:
        case NOTIFICATION_TYPE.COMMENT_ANSWER:
          const commentPayload = payload as PushCommentPayload;
          const commentTaskId = commentPayload.taskId;
          if (commentTaskId == null) return console.error('taskId is not defined!');

          await this.navigateToPushTarget(['/tasks', commentTaskId, 'comments'], coldStart);
          break;
        case NOTIFICATION_TYPE.OFFER:
        case NOTIFICATION_TYPE.EXTRA_WORK:
        case NOTIFICATION_TYPE.CHAT:
        case NOTIFICATION_TYPE.EXTRA_WORK_REJECTED:
        case NOTIFICATION_TYPE.EXTRA_WORK_OFFER_EXTRA_PAY:
        case NOTIFICATION_TYPE.EXTRA_WORK_OWNER_APPROVE:
        case NOTIFICATION_TYPE.EXTRA_WORK_PAID:
        case NOTIFICATION_TYPE.EXTRA_WORK_WORKER_APPROVE:
        case NOTIFICATION_TYPE.EXTRA_WORK_PENDING:
        case NOTIFICATION_TYPE.EXTRA_WORK_WORKER_ADDED_RESULTS:
        case NOTIFICATION_TYPE.EXTRA_WORK_WAIT_PAY:
        case NOTIFICATION_TYPE.DISPUTE_CANCELED:
        case NOTIFICATION_TYPE.EXTRA_WORK_DELETED:
        case NOTIFICATION_TYPE.EXTRA_WORK_OWNER_APPROVE_RESULTS:
        case NOTIFICATION_TYPE.PAYMENT_CANCELED:
        case NOTIFICATION_TYPE.PAYMENT_AFTER_5_DAYS:
          const chatPayload = payload as PushChatPayload;
          const chatNotificationChatId = chatPayload.chatId;
          if (chatNotificationChatId == null) return console.error('chatId is not defined!');

          await this.navigateToPushTarget(['/chats', chatNotificationChatId], coldStart);
          break;
        case NOTIFICATION_TYPE.REPLY:
        case NOTIFICATION_TYPE.STATUS:
        case NOTIFICATION_TYPE.INACTIVITY_EXTRA_WORK:
        case NOTIFICATION_TYPE.INACTIVITY_DEADLINE:
        case NOTIFICATION_TYPE.INACTIVITY_OWNER:
        case NOTIFICATION_TYPE.DISPUTED:
        case NOTIFICATION_TYPE.DEADLINE:
        case NOTIFICATION_TYPE.REPLY_DATA_UPDATED:
          const replyPayload = payload as PushReplyPayload;
          const replyId = replyPayload.replyId;
          if (replyId == null) return console.error('replyId is not defined!');

          await this.navigateToPushTarget(['/reply', replyId], coldStart);
          break;
        case NOTIFICATION_TYPE.TASK:
        case NOTIFICATION_TYPE.OFFER_STATUS_ACCEPTED:
        case NOTIFICATION_TYPE.OFFER_STATUS_REJECTED:
        case NOTIFICATION_TYPE.PAY_FOR_TRUSTED_USER:
        case NOTIFICATION_TYPE.PAYMENT_SUCCEEDED_REPLY_TIPS:
        case NOTIFICATION_TYPE.TASK_UNPUBLISHED:
        case NOTIFICATION_TYPE.TASK_WILL_BE_UNPUBLISHED_AFTER_5_DAYS: {
          const taskPayload = payload as PushTaskPayload;
          const taskId = taskPayload.taskId;
          if (taskId == null) return console.error('taskId is not defined!');

          await this.navigateToPushTarget(['/tasks', taskId], coldStart);
          break;
        }
        case NOTIFICATION_TYPE.DISPUTED_TASK_FOR_TRUSTED_USER: {
          const taskPayload = payload as PushTaskPayload;
          const taskId = taskPayload.taskId;
          const replyId = taskPayload.replyId;
          if (taskId == null) return console.error('taskId is not defined!');

          if (replyId == null) {
            await this.navigateToPushTarget(['/tasks', taskId], coldStart);
            break;
          }

          await this.navigateToPushTarget(['/tasks'], coldStart);
          await this.injector.get(DisputeAssignmentService).confirmAndAssign(replyId, taskId);
          break;
        }
        case NOTIFICATION_TYPE.PAYMENT_SUCCEEDED:
        case NOTIFICATION_TYPE.PAYMENT_FAILED:
          const paymentPayload = payload as PushPaymentPayload;
          const paymentId = paymentPayload.paymentId;
          if (paymentId == null) return console.error('paymentId is not defined!');

          await this.navigateToPushTarget(['/wallet'], coldStart);
          break;
        case NOTIFICATION_TYPE.ONBOARDING:
          // if (!data?.onboardingId) return console.error('onboardingId is not defined!');
          //
          // await this.router.navigate(['/profile/balance']);
          break;
        case NOTIFICATION_TYPE.OFFER_CREATE_TASK:
          const offerPayload = payload as PushOfferCreateTaskPayload;
          const categoryId = offerPayload.categoryId;
          if (categoryId == null) return console.error('categoryId is not defined!');

          await this.navigateToPushTarget(['/tasks/add/category'], coldStart);
          break;
        case NOTIFICATION_TYPE.CALL:
          const callPayload = payload as PushCallPayload;
          const callOrderId = callPayload.callOrderId ?? callPayload.avatarOrderId;
          if (callOrderId == null) return console.error('callOrderId is not defined!');

          await this.goToCall(callOrderId, CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL);
          break;
        case NOTIFICATION_TYPE.CALL_CANCELLED: {
          const callCancelledPayload = payload as PushCallCancelPayload;
          const isMissedCallRaw = callCancelledPayload.isMissedCall;
          const isMissedCall = (isMissedCallRaw === true) || (this.normalizePayloadNumber(isMissedCallRaw ?? null) === 1);

          if (isMissedCall) {
            await this.navigateToPushTarget(['/calls'], coldStart);
          }
          break;
        }
        case NOTIFICATION_TYPE.AVATAR_ORDER:
          const avatarPayload = payload as PushAvatarOrderPayload;
          const avatarOrderId = avatarPayload.avatarOrderId;
          if (avatarOrderId == null) return console.error('avatarOrderId is not defined!');

          await this.navigateToPushTarget(['/avatar/order-details', avatarOrderId], coldStart);
          break;
        case NOTIFICATION_TYPE.AVATAR_ORDER_CANCELLED: {
          const avatarCancelledPayload = payload as PushAvatarOrderPayload;
          const isMissedCallRaw = avatarCancelledPayload.isMissedCall;
          const isMissedCall = (isMissedCallRaw === true) || (this.normalizePayloadNumber(isMissedCallRaw ?? null) === 1);

          if (isMissedCall) {
            await this.navigateToPushTarget(['/calls'], coldStart);
            break;
          }

          const cancelledAvatarOrderId = avatarCancelledPayload.avatarOrderId;
          if (cancelledAvatarOrderId == null) {
            console.warn('Unknown notification type:', notificationType);
            break;
          }
          await this.navigateToPushTarget(['/avatar/order-details', cancelledAvatarOrderId], coldStart);
          break;
        }
        case NOTIFICATION_TYPE.ACCOUNT_PAYPAL_INVALID:
          await this.navigateToPushTarget(['/wallet'], coldStart);
          break;
        case NOTIFICATION_TYPE.SUBSCRIPTION_MESSAGE:
          const subscriptionPayload = payload as PushSubscriptionPayload;
          const videoUrl = subscriptionPayload.url;
          const videoId = subscriptionPayload.video_id;
          const subscriptionContentType = this.normalizePayloadString(subscriptionPayload.content_type);
          const subscriptionContentId = this.normalizePayloadNumber(subscriptionPayload.content_id ?? null);

          if (subscriptionContentType === CONTENT_TYPE.FILE && subscriptionContentId != null) {
            await this.navigateToFileFromNotification(subscriptionContentId, coldStart);
          } else if (subscriptionContentType === CONTENT_TYPE.VIDEO && subscriptionContentId != null) {
            await this.navigateToStreamById(subscriptionContentId, coldStart);
          } else if (videoUrl) {
            await this.navigateByNotificationUrl(videoUrl, coldStart);
          } else if (videoId) {
            await this.navigateToVideoFromNotification(videoId, coldStart);
          } else {
            return console.error('Data is not defined!');
          }
          break;
        case NOTIFICATION_TYPE.USER_ACTIONS_LIKE:
        case NOTIFICATION_TYPE.NEW_CONTENT_COMMENT:
        case NOTIFICATION_TYPE.NEW_CONTENT_COMMENT_ANSWER: {
          const userActionPayload = payload as PushUserActionPayload;
          const contentType = userActionPayload.content_type;
          const normalizedContentType = this.normalizePayloadString(contentType);
          const normalizedContentId = this.normalizePayloadNumber(userActionPayload.content_id ?? null);

          if (!normalizedContentType) return console.error('content_type is not defined!');
          if (normalizedContentId == null) return console.error('content_id is not defined!');

          switch (normalizedContentType) {
            case CONTENT_TYPE.VIDEO:
              await this.navigateToStreamById(normalizedContentId, coldStart);
              break;
            case CONTENT_TYPE.FILE:
              await this.navigateToFileById(normalizedContentId, coldStart);
              break;
          }
          break;
        }
        case NOTIFICATION_TYPE.NEW_SUBSCRIBER:
          const newSubscriberPayload = payload as PushNewSubscriberPayload;
          const profilePath = buildProfilePath({
            userId: newSubscriberPayload.user_id ?? null,
          });
          if (!profilePath) return;

          await this.navigateToPushTarget(profilePath, coldStart);
          break;
        case NOTIFICATION_TYPE.UNIVERSAL:
          const universalPayload = payload as PushUniversalPayload;
          const universalUrl = universalPayload.url;
          if (!universalUrl) return console.error('URL is not defined!');

          await this.navigateByNotificationUrl(universalUrl, coldStart);
          break;
        default:
          console.warn('Unknown notification type:', notificationType);
          break;
      }
    });
  }

  private navigateToPushTarget(
    url: string | unknown[],
    coldStart: boolean,
  ): Promise<boolean> {
    return this.appNavigation.navigateFromExternalEntry(url, {
      animated: coldStart ? false : undefined,
      coldStart,
    });
  }

  private async goToCall(orderId: number, orderType: number | null = null): Promise<void> {
    console.log('Notification service, call push, orderId', orderId);
    const navigationCommands: (string | number)[] = await this.resolveIncomingOrderNavigationCommands(orderId, orderType);

    await this.ngZone.run(async (): Promise<void> => {
      if (this.shouldSaveCallBackUrl(this.router.url)) {
        this.navigationStore.setBackUrl(this.router.url);
      }
      await this.appNavigation.navigateForward(navigationCommands, {replaceUrl: true});
    });
  }

  private async resolveIncomingOrderNavigationCommands(orderId: number, orderType: number | null): Promise<(string | number)[]> {
    if (this.isAudioCallType(orderType)) {
      return ['/incoming-call', orderId];
    }
    if (typeof orderType === 'number') {
      return ['/avatar/order-details', orderId];
    }

    try {
      const response: AvatarOrderDataResponse = await this.avatarOrderService.getOrder(orderId, false);
      const resolvedOrderType: number | null = this.normalizePayloadNumber(response?.data?.type ?? null);
      if (this.isAudioCallType(resolvedOrderType)) {
        return ['/incoming-call', orderId];
      }
    } catch (error) {
      console.error('Notification service, failed to resolve order type', { orderId, error });
    }

    return ['/avatar/order-details', orderId];
  }

  private isAudioCallType(orderType: number | null): boolean {
    return orderType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  private normalizeIncomingOrderType(rawType: number | null): number | null {
    if (rawType === null) return null;
    if (rawType < 0) return null;
    if (rawType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) return rawType;
    if (rawType === NOTIFICATION_TYPE.CALL) return CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
    return rawType;
  }

  private shouldSaveCallBackUrl(url: string): boolean {
    const normalizedUrl: string = url.toLowerCase();
    return !normalizedUrl.includes('/incoming-call')
      && !normalizedUrl.includes('/audio-call')
      && !normalizedUrl.includes('/video-call');
  }

  public async navigateToFileFromNotification(contentId: unknown, coldStart: boolean = false): Promise<boolean> {
    const normalizedContentId = this.normalizePayloadNumber(contentId ?? null);
    if (normalizedContentId == null) {
      console.error('content_id is not defined!');
      return false;
    }

    return this.navigateToFileById(normalizedContentId, coldStart);
  }

  public async navigateToVideoFromNotification(contentId: unknown, coldStart: boolean = false): Promise<boolean> {
    const normalizedContentId = this.normalizePayloadNumber(contentId ?? null);
    if (normalizedContentId == null) {
      console.error('content_id is not defined!');
      return false;
    }

    return this.navigateToStreamById(normalizedContentId, coldStart);
  }

  public async navigateByNotificationUrl(
    url: string | null | undefined,
    coldStart: boolean = false,
  ): Promise<void> {
    if (!url) {
      return;
    }

    const result: ProfileMediaOpenResult = await this.profileMediaNavigationService.openFromProfileMediaUrlWithResult(
      url,
      {coldStart},
    );
    if (result.opened) {
      return;
    }

    if (result.reason === 'not-found') {
      await this.showProfileMediaUnavailableToast();
      return;
    }

    if (result.reason === 'unsupported' && result.fileId != null && result.userId != null) {
      await this.navigateToProfileFileFallback(result.userId, result.fileId, coldStart);
      return;
    }

    if (result.contentType === CONTENT_TYPE.FILE && result.fileId != null && result.userId != null) {
      await this.navigateToVplayFileRoute(result.userId, result.fileId, coldStart);
      return;
    }

    await this.navigateToPushTarget(this.normalizeUrlWithParams(url), coldStart);
  }

  private async navigateToFileById(fileId: number, coldStart: boolean = false): Promise<boolean> {
    const result: ProfileMediaOpenResult = await this.profileMediaNavigationService.openFileByIdWithResult(
      fileId,
      {coldStart},
    );
    if (!result.opened) {
      if (result.reason === 'not-found') {
        await this.showProfileMediaUnavailableToast();
        return false;
      }
      if (result.reason === 'unsupported' && result.userId != null && result.fileId != null) {
        await this.navigateToProfileFileFallback(result.userId, result.fileId, coldStart);
        return true;
      }
      if (result.userId != null && result.fileId != null) {
        await this.navigateToVplayFileRoute(result.userId, result.fileId, coldStart);
        return true;
      }
      console.warn('Profile media file cannot be opened in vplay', { fileId });
    }
    return result.opened;
  }

  private async navigateToProfileFileFallback(
    userId: number,
    fileId: number,
    coldStart: boolean = false,
  ): Promise<void> {
    const currentUserId: number | null = this.authService.parsedUser?.id ?? null;
    const profilePath: string = currentUserId === userId
      ? '/profile'
      : (buildProfilePath({ userId }) ?? `/people/${userId}`);

    await this.appNavigation.navigateFromExternalEntry([profilePath], {
      coldStart,
      queryParams: {
        tab: CONTENT_MODE.FILES,
        file_id: fileId,
      },
    });
  }

  private async navigateToVplayFileRoute(
    userId: number,
    fileId: number,
    coldStart: boolean = false,
  ): Promise<void> {
    await this.navigateToPushTarget(['/avatar', 'vplay', userId, fileId], coldStart);
  }

  private async navigateToStreamById(videoId: number, coldStart: boolean = false): Promise<boolean> {
    const opened: boolean = await this.profileMediaNavigationService.openStreamById(videoId, {coldStart});
    if (!opened) {
      console.warn('Profile stream cannot be opened in vplay', { videoId });
      return this.navigateToPushTarget(['/avatar/vplay', videoId], coldStart);
    }
    return opened;
  }

  private async showProfileMediaUnavailableToast(): Promise<void> {
    await this.toastService.warning(this.translate.instant('notificationService.toasts.profileMediaUnavailable'));
  }

  public async getNotificationPermissionStatusWithBrowser(): Promise<boolean> {
    if ((typeof window !== 'undefined') && !('Notification' in window)) {
      const errorMessage: string = this.translate.instant('notificationService.toasts.errorNotificationsNotSupported');
      console.error(errorMessage);
      await this.toastService.error(errorMessage);
      return false;
    }

    if ((typeof navigator == 'undefined') || !('permissions' in navigator)) {
      console.warn('API Permissions is not supported');
      this.#pushPermissionGrantedSubject.next(null);
      return true;
    }

    let result: boolean = false;
    try {
      const notificationsPermissionStatus: PermissionStatus = await navigator.permissions.query({name: 'notifications' as PermissionName});
      switch (notificationsPermissionStatus.state) {
        case 'granted':
          console.log('Access to notifications granted.');
          result = true;
          break;
        case 'prompt':
          console.info('The permission request for geolocation is in a prompt state.');
          result = await this.requestNotificationPermissionWithBrowser();
          break;
        case 'denied':
          console.warn('Access to notifications denied.');
          await this.toastService.warning(this.translate.instant('notificationService.toasts.allowBrowserNotifications'));
          break;
      }
      this.#pushPermissionGrantedSubject.next(result);
      return result;
    } catch (error) {
      const errorMessage: string = this.translate.instant('notificationService.toasts.errorCheckingNotificationsPermissions');
      console.error(errorMessage);
      await this.toastService.error(errorMessage);
      this.#pushPermissionGrantedSubject.next(false);
      throw error;
    }
  }

  private async requestNotificationPermissionWithBrowser(): Promise<boolean> {
    let result: boolean = false;
    try {
      if (typeof window !== 'undefined') {
        const notificationPermission: NotificationPermission = await Notification.requestPermission();
        switch (notificationPermission) {
          case 'granted':
            console.log('Access to notification granted');
            result = true;
            break;
          case 'default':
            console.info('Access to notification default');
            break;
          case 'denied':
            console.warn('Access to notification denied');
            break;
        }
      }
      return result;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  private async registerPush(): Promise<void> {
    await PushNotifications.register(); // Register with Apple / Google to receive push via APNS/FCM
  }

  public async registerPushIfNeedAndSendPushToken(): Promise<void> {
    try {
      this.pushToken
        ? await this.sendPushToken(this.pushToken)
        : await this.registerPush();
    } catch (error) {
      console.error('Error on register push if need and send push token', error);
      throw error;
    }
  }

  public normalizeUrlWithParams(encodedUrl: string): string {
    if (!encodedUrl) {
      return '';
    }
    const decodedUrl = decodeURIComponent(encodedUrl);
    const urlObj = new URL(decodedUrl, window.location.origin);
    const normalizedPath = this.normalizeStreamPath(urlObj.pathname);
    return normalizedPath + urlObj.search;
  }

  private normalizeStreamPath(pathname: string): string {
    if (pathname.startsWith('/avatar/consumer/')) {
      return pathname.replace('/avatar/consumer/', '/avatar/vplay/');
    }
    if (pathname === '/avatar/consumer') {
      return '/avatar/vplay';
    }
    return pathname;
  }
}
